// frontend/src/features/ups/modals/InternalDriverFormModal.js
import React, { useEffect, useMemo, useState } from 'react';

// ==========================================
// SECTION 1: OUT-OF-COMPONENT HELPERS
// Defined outside the component so they don't recreate on every render
// ==========================================

// Safely extracts data if editing, or sets empty strings if creating
function initialForm(driver) {
  return {
    name: driver?.name || '',
    vehicle_registration_number: driver?.vehicle_registration_number || '',
    phone: driver?.phone || '',
    email: driver?.email || '',
  };
}

// Runs business logic checks before allowing submission
function validate(form) {
  const errors = {};

  // Clean data before validating
  const name = form.name.trim();
  const vrn = form.vehicle_registration_number.trim();
  const phone = form.phone.trim();
  const email = form.email.trim();

  // Required fields
  if (!name) errors.name = 'Name is required.';
  if (!vrn) errors.vehicle_registration_number = 'Vehicle registration number is required.';

  // Format checks
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (phone && phone.length > 50) {
    errors.phone = 'Phone number is too long.';
  }

  return errors;
}

// ==========================================
// SECTION 2: COMPONENT SETUP & STATE
// ==========================================
export default function InternalDriverFormModal({
  isOpen,
  onClose,
  onSubmit, // Parent provides this function to handle the actual API call
  driver = null,
  submitting = false,
}) {
  // Determine mode based on whether an ID exists
  const isEdit = Boolean(driver?.id);

  // Local component state
  const [form, setForm] = useState(initialForm(driver));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(''); // Holds backend API errors

  // Reset the form cleanly whenever the modal opens or the driver changes
  useEffect(() => {
    if (!isOpen) return;
    setForm(initialForm(driver));
    setErrors({});
    setSubmitError('');
  }, [isOpen, driver]);

  // Dynamically change title without recalculating unnecessarily 
  const title = useMemo(
    () => (isEdit ? 'Edit Internal Driver' : 'New Internal Driver'),
    [isEdit]
  );

  // Early return: don't render anything if closed
  if (!isOpen) return null;

  // ==========================================
// SECTION 3: EVENT HANDLERS
// ==========================================

  // Universal updater for inputs that also clears active errors
  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Stop standard HTML form submission

    // 1. Validate
    const nextErrors = validate(form);
    setErrors(nextErrors);
    setSubmitError('');

    // 2. Stop if invalid
    if (Object.keys(nextErrors).length > 0) return;

    // 3. Attempt to submit clean data
    try {
      await onSubmit({
        name: form.name.trim(),
        vehicle_registration_number: form.vehicle_registration_number.trim(),
        phone: form.phone.trim() || null, // Convert empty strings to null for DB
        email: form.email.trim() || null,
      });
    } catch (err) {
      // Extract the error message from the backend response
      setSubmitError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to save internal driver.'
      );
    }
  };

  // ==========================================
// SECTION 4: UI / RENDER
// ==========================================
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Dark background overlay (click to close) */}
      <button
        type="button"
        aria-label="Close modal backdrop"
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (!submitting) onClose();
        }}
      />

      {/* Main Modal Box */}
      <div className="relative z-10 w-full max-w-lg rounded-xl bg-white shadow-xl border">
        
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
            <p className="mt-1 text-sm text-gray-500">
              Company-owned internal driver record.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          
          {/* Top-level Backend Error Alert */}
          {submitError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded p-3">
              {submitError}
            </div>
          )}

          {/* Input: Name */}
          <div>
            <label
              htmlFor="internal-driver-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="internal-driver-name"
              name="name"
              type="text"
              autoComplete="name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              disabled={submitting}
              className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring ${
                errors.name ? 'border-red-300 bg-red-50' : ''
              }`}
              placeholder="Driver name"
            />
            {errors.name && (
              <div className="mt-1 text-xs text-red-600">{errors.name}</div>
            )}
          </div>

          {/* Input: VRN (auto-converts to uppercase) */}
          <div>
            <label
              htmlFor="internal-driver-vrn"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Vehicle Registration Number <span className="text-red-500">*</span>
            </label>
            <input
              id="internal-driver-vrn"
              name="vehicle_registration_number"
              type="text"
              value={form.vehicle_registration_number}
              onChange={(e) =>
                setField('vehicle_registration_number', e.target.value.toUpperCase())
              }
              disabled={submitting}
              className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring ${
                errors.vehicle_registration_number ? 'border-red-300 bg-red-50' : ''
              }`}
              placeholder="AB12 CDE"
            />
            {errors.vehicle_registration_number && (
              <div className="mt-1 text-xs text-red-600">
                {errors.vehicle_registration_number}
              </div>
            )}
          </div>

          {/* Split Inputs: Phone and Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="internal-driver-phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone
              </label>
              <input
                id="internal-driver-phone"
                name="phone"
                type="text"
                autoComplete="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                disabled={submitting}
                className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring ${
                  errors.phone ? 'border-red-300 bg-red-50' : ''
                }`}
                placeholder="07123 456789"
              />
              {errors.phone && (
                <div className="mt-1 text-xs text-red-600">{errors.phone}</div>
              )}
            </div>

            <div>
              <label
                htmlFor="internal-driver-email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <input
                id="internal-driver-email"
                name="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                disabled={submitting}
                className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring ${
                  errors.email ? 'border-red-300 bg-red-50' : ''
                }`}
                placeholder="driver@example.com"
              />
              {errors.email && (
                <div className="mt-1 text-xs text-red-600">{errors.email}</div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-50 transition text-sm disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm disabled:opacity-50"
            >
              {submitting
                ? isEdit
                  ? 'Saving…'
                  : 'Creating…'
                : isEdit
                ? 'Save changes'
                : 'Create internal driver'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}